import { GlassPanel } from '@/components/brand/glass-panel';
import { SocialConnections } from '@/components/social-connections';
import { Button } from '@sixam/ui/button';
import { Input } from '@sixam/ui/input';
import { Label } from '@sixam/ui/label';
import { Separator } from '@sixam/ui/separator';
import { Text } from '@sixam/ui/text';
import { BRAND } from '@sixam/config/brand';
import { captureContact } from '@/lib/contacts';
import { getAuthRedirectUri, getEmailFieldError } from '@sixam/supabase/auth';
import { supabase } from '@sixam/supabase/client';
import { router } from 'expo-router';
import { useAnalytics } from '@/components/analytics-provider';
import { CheckIcon } from 'lucide-react-native';
import * as React from 'react';
import { Pressable, TextInput, View } from 'react-native';
import { authDestination, signInHref, validatedInternalReturnPath } from '@/lib/auth-return';
import { TERMS_SUMMARY, TERMS_VERSION } from '@sixam/shared/community-terms';
import { recordTermsAcceptance } from '@/lib/community-terms-client';

export function SignUpForm({ returnTo }: Readonly<{ returnTo?: string | string[] }>) {
  const posthog = useAnalytics();
  const passwordInputRef = React.useRef<TextInput>(null);
  const [email, setEmail] = React.useState('');
  const [password, setPassword] = React.useState('');
  const [marketingConsent, setMarketingConsent] = React.useState(true);
  // Unticked by default: agreeing has to be a deliberate act, not something the
  // form did on the member's behalf.
  const [termsAccepted, setTermsAccepted] = React.useState(false);
  const [error, setError] = React.useState<{ email?: string; password?: string; terms?: string }>(
    {}
  );
  const [isSubmitting, setIsSubmitting] = React.useState(false);

  function onEmailSubmitEditing() {
    passwordInputRef.current?.focus();
  }

  async function onSubmit() {
    if (isSubmitting) {
      return;
    }

    // No account is created until the terms are agreed to.
    if (!termsAccepted) {
      setError({ terms: 'Please agree to the community terms to create an account.' });
      return;
    }

    setIsSubmitting(true);
    setError({});

    try {
      const normalizedEmail = email.trim();
      const { data, error: signUpError } = await supabase.auth.signUp({
        email: normalizedEmail,
        password,
        options: { emailRedirectTo: getAuthRedirectUri() },
      });

      if (signUpError) {
        setError(getEmailFieldError(signUpError.message));
        return;
      }

      posthog.capture('signed_up', {
        marketing_consent: marketingConsent,
        terms_version: TERMS_VERSION,
      });

      // Recorded against the profile so we can prove what was agreed and when,
      // and re-prompt if the terms change materially.
      await recordTermsAcceptance().catch(() => {
        // Agreement already gated account creation; a failed write here is
        // repaired by the terms gate on next sign-in.
      });

      await captureContact({
        consentStatus: marketingConsent ? 'opted_in' : 'opted_out',
        email: normalizedEmail,
        source: 'signup',
      }).catch(() => {
        // Signup should not fail if marketing capture is temporarily unavailable.
      });

      if (!data.session) {
        const safeReturnTo = validatedInternalReturnPath(returnTo);
        router.push({
          pathname: '/(public)/verify-email',
          params: { email: normalizedEmail, ...(safeReturnTo ? { returnTo: safeReturnTo } : {}) },
        });
      } else {
        router.replace(authDestination(returnTo));
      }
    } catch (submitError) {
      setError({
        password:
          submitError instanceof Error ? submitError.message : 'Could not create your account.',
      });
    } finally {
      setIsSubmitting(false);
    }
  }

  return (
    <View>
      <GlassPanel className="rounded-xl" contentClassName="gap-7 px-5 py-6 sm:px-6 sm:py-7">
        <View className="gap-2">
          <Text className="font-sans text-xs font-bold uppercase leading-none text-white/70">
            Create account
          </Text>
          <Text
            variant="h2"
            className="normal-case leading-none text-white"
            style={{ fontSize: 30 }}
          >
            Join 6am BKK
          </Text>
          <Text className="max-w-[310px] text-sm leading-5 text-white/80">
            Set up your member access for routes, sessions, and run-day check-ins.
          </Text>
        </View>
        <View className="gap-6">
          <View className="gap-6">
            <View className="gap-1.5">
              <Label
                className="font-sans text-xs font-bold uppercase text-white/75"
                htmlFor="email"
              >
                Email
              </Label>
              <Input
                id="email"
                placeholder="runner@6ambkk.com"
                keyboardType="email-address"
                autoComplete="email"
                autoCapitalize="none"
                value={email}
                onChangeText={setEmail}
                onSubmitEditing={onEmailSubmitEditing}
                returnKeyType="next"
                submitBehavior="submit"
              />
              {error.email ? (
                <Text className="text-sm text-destructive" selectable>
                  {error.email}
                </Text>
              ) : null}
            </View>
            <View className="gap-1.5">
              <View className="flex-row items-center">
                <Label
                  className="font-sans text-xs font-bold uppercase text-white/75"
                  htmlFor="password"
                >
                  Password
                </Label>
              </View>
              <Input
                ref={passwordInputRef}
                id="password"
                secureTextEntry
                value={password}
                onChangeText={setPassword}
                returnKeyType="send"
                onSubmitEditing={onSubmit}
              />
              {error.password ? (
                <Text className="text-sm text-destructive" selectable>
                  {error.password}
                </Text>
              ) : null}
            </View>
            <Pressable
              accessibilityRole="checkbox"
              accessibilityState={{ checked: marketingConsent, disabled: isSubmitting }}
              className="flex-row items-start gap-3 rounded-xl border border-white/25 bg-black/35 p-4 active:opacity-80"
              disabled={isSubmitting}
              onPress={() => setMarketingConsent((current) => !current)}
            >
              <View
                className="mt-0.5 h-5 w-5 items-center justify-center rounded border border-primary"
                style={{ backgroundColor: marketingConsent ? BRAND.color.orange : 'transparent' }}
              >
                {marketingConsent ? <CheckIcon color="white" size={14} /> : null}
              </View>
              <View className="min-w-0 flex-1 gap-1">
                <Text className="font-sans text-xs font-bold uppercase tracking-widest text-white">
                  Club updates
                </Text>
                <Text className="text-sm leading-5 text-white/80">
                  Send me event drops, route notes, and 6am BKK updates.
                </Text>
              </View>
            </Pressable>
            <Pressable
              accessibilityHint="Required to create an account"
              accessibilityLabel="I agree to the community terms"
              accessibilityRole="checkbox"
              accessibilityState={{ checked: termsAccepted, disabled: isSubmitting }}
              className="flex-row items-start gap-3 rounded-xl border border-white/25 bg-black/35 p-4 active:opacity-80"
              disabled={isSubmitting}
              onPress={() => setTermsAccepted((current) => !current)}
            >
              <View
                className="mt-0.5 h-5 w-5 items-center justify-center rounded border border-primary"
                style={{ backgroundColor: termsAccepted ? BRAND.color.orange : 'transparent' }}
              >
                {termsAccepted ? <CheckIcon color="white" size={14} /> : null}
              </View>
              <View className="min-w-0 flex-1 gap-1">
                <Text className="font-sans text-xs font-bold uppercase tracking-widest text-white">
                  Community terms
                </Text>
                <Text className="text-sm leading-5 text-white">
                  I agree to the community terms. {TERMS_SUMMARY}
                </Text>
              </View>
            </Pressable>
            <Pressable
              accessibilityRole="link"
              className="active:opacity-70"
              onPress={() => router.push('/terms')}
            >
              <Text className="font-sans text-xs font-bold uppercase tracking-widest text-primary underline underline-offset-4">
                Read the full terms
              </Text>
            </Pressable>
            {error.terms ? (
              <Text className="text-sm text-destructive" selectable>
                {error.terms}
              </Text>
            ) : null}
            <Button
              className="h-14 w-full"
              onPress={onSubmit}
              disabled={isSubmitting || !termsAccepted}
            >
              <Text>{isSubmitting ? 'Creating account...' : 'Create account'}</Text>
            </Button>
          </View>
          <Text className="text-center text-sm text-white/75">
            Already a member?{' '}
            <Pressable
              onPress={() => router.push(signInHref(validatedInternalReturnPath(returnTo)))}
            >
              <Text className="font-sans text-xs font-bold uppercase tracking-widest text-primary underline underline-offset-4">
                Sign in
              </Text>
            </Pressable>
          </Text>
          <View className="flex-row items-center">
            <Separator className="bg-white/18 flex-1" />
            <Text className="px-4 font-mono text-xs uppercase text-white/60">or</Text>
            <Separator className="bg-white/18 flex-1" />
          </View>
          <SocialConnections returnTo={returnTo} termsAccepted={termsAccepted} />
        </View>
      </GlassPanel>
    </View>
  );
}
